import { useEffect, useMemo, useState } from 'react';
import { Link, createFileRoute, notFound } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Plus, Printer, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  Detail,
  DetailList,
  ErrorState,
  Page,
  PageBody,
  PageHeader,
  Panel,
  Skeleton,
} from '@/ui/Panel';
import { Button } from '@/ui/Button';
import { Checkbox, Field, Input, MoneyInput, Select, Switch } from '@/ui/Field';
import { Badge, Code } from '@/ui/Badge';
import { ConfirmDialog } from '@/ui/Dialog';
import { api, errorMessage } from '@/lib/api/client';
import { useShortcutHint } from '@/app/StatusLine';
import {
  dateTime,
  humanize,
  marginPct,
  money,
  num,
  percent,
  qty,
  smartDate,
} from '@/lib/format';
import { LastCounted } from '@/features/inventory/common';
import type {
  BrandSummary,
  DepartmentListResponse,
  ProductBarcode,
  ProductDetail,
  ProductDetailWithMedia,
  StoreZoneSummary,
} from '@/lib/api/types';
import { ProductPurchases } from '@/features/catalog/ProductPurchases';
import { ProductPhotos } from '@/features/catalog/ProductPhotos';
import { WhoSellsItPanel } from '@/features/vendor-catalogs/WhoSellsItPanel';
import { activeFamilies, useFamilies } from '@/features/catalog/families';
import {
  BecomesPanel,
  InputsPanel,
  inputsLastPaidSummary,
} from '@/features/catalog/InputLinksPanel';
import { INPUT_CONSEQUENCE } from '@/features/buying/NewInputProductDialog';
import { Dialog } from '@/ui/Dialog';

/* The full product editor.

   Everything editable is in one dirty-tracked form saved by a single PUT, and
   the things that are their own records — barcodes — mutate immediately,
   because an operator who adds a barcode expects it to exist, not to be
   staged behind a Save button. */

export const Route = createFileRoute('/_app/products_/$id')({
  staticData: { crumb: 'Product' },
  /**
   * Product ids are integers. Anything else is a stale bookmark from the old
   * admin's `/products/departments|brands|barcodes|labels`, which this route
   * happily captured as `id="departments"` and then rendered as a Product
   * screen showing the API's raw Zod message — an internal type error in an
   * operator's face.
   *
   * The known legacy segments are rewritten by `__root.tsx` before this runs;
   * this guard catches everything else, so a typo gets the friendly not-found
   * screen instead of a broken detail view.
   */
  beforeLoad: ({ params }) => {
    if (!/^\d+$/.test(params.id)) throw notFound();
  },
  component: ProductEditScreen,
});

/** Canonical size units, mirrored from the API's unit-normalize map. */
const SIZE_UNITS = [
  'ct',
  'ea',
  'ft',
  'g',
  'gal',
  'in',
  'kg',
  'l',
  'lb',
  'ml',
  'oz',
  'pc',
  'pk',
  'pt',
  'qt',
] as const;

const STORAGE_TYPES = ['shelf', 'refrigerated', 'frozen'] as const;
const PRICE_TYPES = ['fixed', 'weight', 'manual'] as const;
const BARCODE_TYPES = ['upc', 'plu', 'alt', 'ean', 'internal'] as const;

interface Draft {
  name: string;
  receiptName: string;
  labelName: string;
  brandId: number | null;
  departmentId: number | null;
  priceType: string;
  price: number;
  cost: number | null;
  sizeValue: number | null;
  sizeUnit: string | null;
  casePricingEnabled: boolean;
  caseQty: number | null;
  casePriceCents: number | null;
  caseBarcode: string;
  zoneId: number | null;
  shelfLocation: string;
  pickSequence: number | null;
  reorderPoint: number | null;
  reorderQty: number | null;
  storageType: string;
  expiryRequired: boolean;
  taxable: boolean;
  taxExempt: boolean;
  ebtEligible: boolean;
  wicEligible: boolean;
  soldByWeight: boolean;
  isPreparedFood: boolean;
  active: boolean;
  /** TASK-0259 (FRESH-1): an input product (never sold) and its family. */
  purchasingOnly: boolean;
  productionFamily: string | null;
}

function toDraft(p: ProductDetail): Draft {
  return {
    name: p.name ?? '',
    receiptName: p.receiptName ?? '',
    labelName: p.labelName ?? '',
    brandId: p.brandId ?? null,
    departmentId: p.departmentId ?? null,
    priceType: p.priceType ?? 'fixed',
    price: p.price ?? 0,
    cost: p.cost ?? null,
    sizeValue: p.sizeValue == null ? null : Number(p.sizeValue),
    sizeUnit: p.sizeUnit ?? null,
    casePricingEnabled: !!p.casePricingEnabled,
    caseQty: p.caseQty ?? null,
    casePriceCents: p.casePriceCents ?? null,
    caseBarcode: p.caseBarcode ?? '',
    zoneId: p.zoneId ?? null,
    shelfLocation: p.shelfLocation ?? '',
    pickSequence: p.pickSequence ?? null,
    reorderPoint: p.reorderPoint ?? null,
    reorderQty: p.reorderQty ?? null,
    storageType: p.storageType ?? 'shelf',
    expiryRequired: !!p.expiryRequired,
    taxable: !!p.taxable,
    taxExempt: !!p.taxExempt,
    ebtEligible: !!p.ebtEligible,
    wicEligible: !!p.wicEligible,
    soldByWeight: !!p.soldByWeight,
    isPreparedFood: !!p.isPreparedFood,
    active: p.active !== false,
    purchasingOnly: !!p.purchasingOnly,
    productionFamily: p.productionFamily ?? null,
  };
}

/** Only the fields the operator actually touched go over the wire. */
function changedFields(base: Draft, draft: Draft): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  const keys = Object.keys(draft) as Array<keyof Draft>;
  for (const key of keys) {
    if (base[key] === draft[key]) continue;
    const value = draft[key];
    if (
      (key === 'receiptName' ||
        key === 'labelName' ||
        key === 'caseBarcode' ||
        key === 'shelfLocation') &&
      value === ''
    ) {
      out[key] = null;
      continue;
    }
    out[key] = value;
  }
  return out;
}

function ProductEditScreen() {
  const { id } = Route.useParams();
  const queryClient = useQueryClient();

  const detail = useQuery({
    queryKey: ['products', id, { days: 90 }],
    queryFn: () =>
      api.get<ProductDetailWithMedia>(`/products/${id}`, { params: { days: 90 } }),
  });

  const departments = useQuery({
    queryKey: ['departments', { pageSize: 500 }],
    queryFn: () =>
      api.get<DepartmentListResponse>('/departments', { params: { pageSize: 500 } }),
    staleTime: 5 * 60_000,
  });
  const brands = useQuery({
    queryKey: ['brands'],
    queryFn: () => api.get<BrandSummary[]>('/brands'),
    staleTime: 5 * 60_000,
  });
  const zones = useQuery({
    queryKey: ['zones'],
    queryFn: () => api.get<StoreZoneSummary[]>('/zones'),
    staleTime: 5 * 60_000,
  });
  const families = useFamilies();
  // TASK-0259: turning Input product off asks for a price first (a sold product needs one).
  const [priceToSell, setPriceToSell] = useState<number | null>(null);
  const [askingPrice, setAskingPrice] = useState(false);

  const product = detail.data;
  const baseline = useMemo(() => (product ? toDraft(product) : null), [product]);
  const [draft, setDraft] = useState<Draft | null>(null);
  const current = draft ?? baseline;

  const dirtyKeys = useMemo(
    () =>
      baseline && draft
        ? (Object.keys(draft) as Array<keyof Draft>).filter(
            (k) => baseline[k] !== draft[k]
          )
        : [],
    [baseline, draft]
  );
  const dirty = dirtyKeys.length > 0;
  const priceDirty = dirtyKeys.includes('price');

  useShortcutHint(dirty ? [{ keys: '⌘S', label: 'Save' }] : []);

  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api.put<ProductDetail>(`/products/${id}`, body),
    onSuccess: (_result, body) => {
      const changedPrice = 'price' in body;
      toast.success('Product saved', {
        description: changedPrice
          ? 'A shelf-label reprint was queued for the new price.'
          : undefined,
      });
      setDraft(null);
      void queryClient.invalidateQueries({ queryKey: ['products'] });
      if (changedPrice) void queryClient.invalidateQueries({ queryKey: ['labels'] });
    },
    onError: (err) =>
      toast.error('Could not save product', { description: errorMessage(err) }),
  });

  // The status line advertises ⌘S, so ⌘S has to actually save.
  const saveNow = save.mutate;
  const canSave = dirty && !save.isPending;
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 's') {
        e.preventDefault();
        if (canSave && baseline && draft) saveNow(changedFields(baseline, draft));
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [canSave, baseline, draft, saveNow]);

  if (detail.isLoading || !current || !baseline) {
    if (detail.error) {
      return (
        <Page>
          <PageHeader title="Product" />
          <PageBody>
            <ErrorState
              title="Could not load this product"
              message={errorMessage(detail.error)}
              onRetry={() => void detail.refetch()}
            />
          </PageBody>
        </Page>
      );
    }
    return (
      <Page>
        <PageHeader title="Product" />
        <PageBody>
          <div className="space-y-3">
            <Skeleton className="h-8 w-1/3" />
            <Skeleton className="h-40 w-full" />
            <Skeleton className="h-40 w-full" />
          </div>
        </PageBody>
      </Page>
    );
  }

  function set<K extends keyof Draft>(key: K, value: Draft[K]) {
    setDraft((prev) => ({ ...(prev ?? baseline!), [key]: value }));
  }

  const margin = current.cost == null ? null : marginPct(current.price, current.cost);
  const priceHistory = [...(product?.priceHistory ?? [])].reverse();
  const salesDays = [...(product?.salesHistory ?? [])].reverse().slice(0, 12);
  const isInput = current.purchasingOnly;
  const inputsLastPaid = inputsLastPaidSummary(product?.inputs ?? []);
  const familyMissing = isInput && !current.productionFamily;

  return (
    <Page>
      <PageHeader
        eyebrow="Catalog"
        title={current.name || 'Untitled product'}
        subtitle={
          product
            ? `${product.department ?? 'No department'}${product.familyName ? ` · ${product.familyName}` : ''} · ${
                product.purchasingOnly
                  ? 'input product, not sold'
                  : product.stock == null
                    ? 'untracked'
                    : `${qty(product.stock)} on hand`
              }`
            : undefined
        }
        actions={
          <>
            <Button size="sm" icon={<ArrowLeft className="size-3.5" />} asChild>
              <Link to="/products" search={{ id }}>
                Back to list
              </Link>
            </Button>
            {dirty ? (
              <Button size="sm" variant="ghost" onClick={() => setDraft(null)}>
                Discard
              </Button>
            ) : null}
            <Button
              size="sm"
              variant="primary"
              disabled={!dirty || familyMissing}
              title={familyMissing ? 'An input product needs a family' : undefined}
              loading={save.isPending}
              onClick={() => save.mutate(changedFields(baseline, current))}
            >
              {dirty
                ? `Save ${dirtyKeys.length} change${dirtyKeys.length === 1 ? '' : 's'}`
                : 'Saved'}
            </Button>
          </>
        }
      />

      <PageBody className="space-y-3">
        <div className="grid gap-3 xl:grid-cols-2">
          <Panel title="Identity">
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Name" required className="sm:col-span-2">
                <Input
                  size="sm"
                  value={current.name}
                  onChange={(e) => set('name', e.currentTarget.value)}
                />
              </Field>
              <Field label="Receipt name" hint="Max 30 characters">
                <Input
                  size="sm"
                  maxLength={30}
                  value={current.receiptName}
                  onChange={(e) => set('receiptName', e.currentTarget.value)}
                />
              </Field>
              <Field label="Label name" hint="Max 30 characters">
                <Input
                  size="sm"
                  maxLength={30}
                  value={current.labelName}
                  onChange={(e) => set('labelName', e.currentTarget.value)}
                />
              </Field>
              <Field label="Department">
                <Select
                  size="sm"
                  value={current.departmentId ?? ''}
                  onChange={(e) =>
                    set(
                      'departmentId',
                      e.currentTarget.value ? Number(e.currentTarget.value) : null
                    )
                  }
                >
                  <option value="">—</option>
                  {(departments.data?.data ?? []).map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Brand">
                <Select
                  size="sm"
                  value={current.brandId ?? ''}
                  onChange={(e) =>
                    set(
                      'brandId',
                      e.currentTarget.value ? Number(e.currentTarget.value) : null
                    )
                  }
                >
                  <option value="">—</option>
                  {(brands.data ?? []).map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field
                label="Family"
                required={isInput}
                hint={
                  isInput
                    ? 'Required for an input product.'
                    : 'Chicken, beef… for Fresh in / out and the pickers. Never for cost or stock.'
                }
                error={familyMissing ? 'An input product needs a family.' : null}
              >
                <Select
                  size="sm"
                  aria-label="Family"
                  value={current.productionFamily ?? ''}
                  onChange={(e) => set('productionFamily', e.currentTarget.value || null)}
                >
                  <option value="">—</option>
                  {activeFamilies(families.data).map((f) => (
                    <option key={f.code} value={f.code}>
                      {f.name}
                    </option>
                  ))}
                  {current.productionFamily &&
                  !activeFamilies(families.data).some(
                    (f) => f.code === current.productionFamily
                  ) ? (
                    <option value={current.productionFamily}>
                      {product?.familyName ?? current.productionFamily} (retired)
                    </option>
                  ) : null}
                </Select>
              </Field>
              <Field label="Status" className="sm:col-span-2">
                <Switch
                  checked={current.active}
                  onCheckedChange={(v) => set('active', v)}
                  label={
                    current.active
                      ? isInput
                        ? 'Active — bought and received; never on a register'
                        : 'Active — sells on the register'
                      : 'Inactive — hidden from the register'
                  }
                />
              </Field>
            </div>
          </Panel>

          <Panel title="Pricing">
            <div className="grid gap-3 sm:grid-cols-3">
              <Field label="Price" required={!isInput}>
                {isInput ? (
                  <div
                    className="text-ink-faint flex h-7 items-center px-1 font-mono text-sm tabular-nums"
                    data-testid="input-price-readonly"
                  >
                    $0.00: inputs are never sold
                  </div>
                ) : (
                  <MoneyInput
                    size="sm"
                    value={current.price}
                    onValueChange={(cents) => set('price', cents ?? 0)}
                  />
                )}
              </Field>
              <Field
                label="Cost"
                hint={
                  inputsLastPaid
                    ? `Inputs last paid: ${inputsLastPaid} — information only; cost is set by hand or by an invoice.`
                    : undefined
                }
              >
                <MoneyInput
                  size="sm"
                  value={current.cost}
                  onValueChange={(cents) => set('cost', cents)}
                />
              </Field>
              <Field label="Margin">
                <div className="flex h-7 items-center px-1 font-mono text-sm tabular-nums">
                  {margin === null ? (
                    <span className="text-ink-faint">—</span>
                  ) : (
                    <span
                      className={
                        margin < 0
                          ? 'text-negative'
                          : margin < 10
                            ? 'text-warn'
                            : 'text-ink'
                      }
                    >
                      {percent(margin)}
                    </span>
                  )}
                </div>
              </Field>
              <Field label="Price type" className="sm:col-span-1">
                <Select
                  size="sm"
                  value={current.priceType}
                  onChange={(e) => set('priceType', e.currentTarget.value)}
                >
                  {PRICE_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {humanize(t)}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Size">
                <Input
                  size="sm"
                  mono
                  inputMode="decimal"
                  value={current.sizeValue ?? ''}
                  onChange={(e) =>
                    set(
                      'sizeValue',
                      e.currentTarget.value === '' ? null : Number(e.currentTarget.value)
                    )
                  }
                />
              </Field>
              <Field label="Unit">
                <Select
                  size="sm"
                  value={current.sizeUnit ?? ''}
                  onChange={(e) => set('sizeUnit', e.currentTarget.value || null)}
                >
                  <option value="">—</option>
                  {SIZE_UNITS.map((u) => (
                    <option key={u} value={u}>
                      {u}
                    </option>
                  ))}
                </Select>
              </Field>
            </div>
            {priceDirty ? (
              <p className="border-warn/30 bg-warn-muted text-ink mt-3 flex items-start gap-2 rounded border p-2 text-xs">
                <Printer className="text-warn mt-0.5 size-3.5 shrink-0" />
                <span>
                  Saving a new price logs the change and{' '}
                  <strong>queues a shelf-label reprint</strong> automatically. The label
                  station picks it up on its next poll — no separate print step.
                </span>
              </p>
            ) : null}
          </Panel>

          <Panel
            title="Case pricing"
            description="Sell a whole case at its own barcode and price."
          >
            <Switch
              className="mb-3"
              checked={current.casePricingEnabled}
              onCheckedChange={(v) => set('casePricingEnabled', v)}
              label="Case pricing enabled"
            />
            <div className="grid gap-3 sm:grid-cols-3">
              <Field label="Units per case">
                <Input
                  size="sm"
                  mono
                  inputMode="numeric"
                  disabled={!current.casePricingEnabled}
                  value={current.caseQty ?? ''}
                  onChange={(e) =>
                    set(
                      'caseQty',
                      e.currentTarget.value === '' ? null : Number(e.currentTarget.value)
                    )
                  }
                />
              </Field>
              <Field label="Case price">
                <MoneyInput
                  size="sm"
                  disabled={!current.casePricingEnabled}
                  value={current.casePriceCents}
                  onValueChange={(cents) => set('casePriceCents', cents)}
                />
              </Field>
              <Field label="Case barcode">
                <Input
                  size="sm"
                  mono
                  disabled={!current.casePricingEnabled}
                  value={current.caseBarcode}
                  onChange={(e) => set('caseBarcode', e.currentTarget.value)}
                />
              </Field>
            </div>
          </Panel>

          <Panel title="Location & inventory">
            <div className="grid gap-3 sm:grid-cols-3">
              <Field label="Zone">
                <Select
                  size="sm"
                  value={current.zoneId ?? ''}
                  onChange={(e) =>
                    set(
                      'zoneId',
                      e.currentTarget.value ? Number(e.currentTarget.value) : null
                    )
                  }
                >
                  <option value="">—</option>
                  {(zones.data ?? [])
                    .filter((z) => z.active !== false || z.id === current.zoneId)
                    .map((z) => (
                      <option key={z.id} value={z.id}>
                        {z.name}
                      </option>
                    ))}
                </Select>
              </Field>
              <Field label="Shelf">
                <Input
                  size="sm"
                  value={current.shelfLocation}
                  onChange={(e) => set('shelfLocation', e.currentTarget.value)}
                />
              </Field>
              <Field label="Pick order" hint="Sequence within the zone">
                <Input
                  size="sm"
                  mono
                  inputMode="numeric"
                  value={current.pickSequence ?? ''}
                  onChange={(e) =>
                    set(
                      'pickSequence',
                      e.currentTarget.value === '' ? null : Number(e.currentTarget.value)
                    )
                  }
                />
              </Field>
              <Field label="Reorder point">
                <Input
                  size="sm"
                  mono
                  inputMode="numeric"
                  value={current.reorderPoint ?? ''}
                  onChange={(e) =>
                    set(
                      'reorderPoint',
                      e.currentTarget.value === '' ? null : Number(e.currentTarget.value)
                    )
                  }
                />
              </Field>
              <Field label="Reorder qty">
                <Input
                  size="sm"
                  mono
                  inputMode="numeric"
                  value={current.reorderQty ?? ''}
                  onChange={(e) =>
                    set(
                      'reorderQty',
                      e.currentTarget.value === '' ? null : Number(e.currentTarget.value)
                    )
                  }
                />
              </Field>
              <Field label="Storage">
                <Select
                  size="sm"
                  value={current.storageType}
                  onChange={(e) => set('storageType', e.currentTarget.value)}
                >
                  {STORAGE_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {humanize(t)}
                    </option>
                  ))}
                </Select>
              </Field>
            </div>
            <div className="border-line-soft mt-3 border-t pt-3">
              <DetailList columns={3}>
                <Detail label="On hand" mono>
                  {product?.purchasingOnly
                    ? 'Not tracked (input product)'
                    : product?.stock == null
                      ? 'Untracked'
                      : qty(product.stock)}
                </Detail>
                <Detail label="Reserved" mono>
                  {qty(product?.reservedQty ?? 0)}
                </Detail>
                <Detail label="Tracking">
                  {product?.purchasingOnly
                    ? 'Not tracked (input product)'
                    : humanize(product?.inventoryTracking ?? 'unclassified')}
                </Detail>
                <Detail label="Last counted">
                  <LastCounted
                    at={product?.lastCountedAt}
                    source={product?.lastCountSource}
                    tracking={product?.inventoryTracking ?? 'unclassified'}
                  />
                </Detail>
              </DetailList>
              <p className="text-2xs text-ink-faint mt-2">
                On-hand is changed from Inventory, never here — a catalog edit must not
                move stock.
              </p>
            </div>
          </Panel>

          <Panel title="Flags">
            <div className="grid gap-2 sm:grid-cols-2">
              <Checkbox
                checked={current.taxable}
                onCheckedChange={(v) => set('taxable', v)}
                label="Taxable"
              />
              <Checkbox
                checked={current.taxExempt}
                onCheckedChange={(v) => set('taxExempt', v)}
                label="Tax exempt"
              />
              <Checkbox
                checked={current.ebtEligible}
                onCheckedChange={(v) => set('ebtEligible', v)}
                label="EBT eligible"
              />
              <Checkbox
                checked={current.wicEligible}
                onCheckedChange={(v) => set('wicEligible', v)}
                label="WIC eligible"
              />
              <Checkbox
                checked={current.soldByWeight}
                onCheckedChange={(v) => set('soldByWeight', v)}
                label="Sold by weight"
              />
              <Checkbox
                checked={current.isPreparedFood}
                onCheckedChange={(v) => set('isPreparedFood', v)}
                label="Prepared food"
              />
              <Checkbox
                checked={current.expiryRequired}
                onCheckedChange={(v) => set('expiryRequired', v)}
                label="Expiry tracked"
              />
              <Checkbox
                className="sm:col-span-2"
                checked={current.purchasingOnly}
                onCheckedChange={(v) => {
                  if (v) {
                    // An input has no price: the draft shows $0.00 and the save sends price 0 with the flag.
                    setDraft((prev) => ({
                      ...(prev ?? baseline!),
                      purchasingOnly: true,
                      price: 0,
                    }));
                  } else if (current.price === 0) {
                    setPriceToSell(null);
                    setAskingPrice(true);
                  } else {
                    set('purchasingOnly', false);
                  }
                }}
                label="Input product (bought for the butcher or kitchen, never sold as is)"
              />
            </div>
            {isInput ? (
              <p className="text-2xs text-ink-muted mt-2" data-testid="input-consequence">
                {INPUT_CONSEQUENCE}
              </p>
            ) : null}
            <p className="text-2xs text-ink-faint mt-3">
              Sold by weight also switches the procurement unit to <Code>lb</Code>, which
              is what purchase-order receiving validates against.
            </p>
          </Panel>

          {product && !product.purchasingOnly ? (
            <InputsPanel
              productId={Number(id)}
              family={product.productionFamily}
              inputs={product.inputs ?? []}
            />
          ) : null}
          {product?.purchasingOnly ? (
            <BecomesPanel
              productId={Number(id)}
              family={product.productionFamily}
              becomes={product.becomes ?? []}
            />
          ) : null}

          <BarcodesPanel productId={id} barcodes={product?.barcodes ?? []} />

          <ProductPhotos
            photos={product?.photos ?? []}
            imageUrl={product?.imageUrl}
            {...(product?.id != null ? { productId: Number(product.id) } : {})}
          />

          <WhoSellsItPanel productId={Number(id)} />

          <ProductPurchases productId={Number(id)} />

          <Panel title="Price history" description={`${priceHistory.length} recorded`}>
            {priceHistory.length === 0 ? (
              <p className="text-2xs text-ink-faint">No price change on record.</p>
            ) : (
              <ul className="space-y-1">
                {priceHistory.slice(0, 12).map((h) => (
                  <li key={h.id} className="flex items-baseline gap-2 text-xs">
                    <span className="text-2xs text-ink-faint w-24 shrink-0 font-mono tabular-nums">
                      {smartDate(h.changedAt)}
                    </span>
                    <span className="text-ink-muted font-mono tabular-nums">
                      {h.oldPrice == null ? '—' : money(h.oldPrice)}
                    </span>
                    <span className="text-ink-faint">→</span>
                    <span className="text-ink font-mono tabular-nums">
                      {money(h.newPrice)}
                    </span>
                    <span className="text-2xs text-ink-faint ml-auto">
                      {humanize(h.source ?? '')}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Panel>

          <Panel
            title="Origin & audit"
            description="Legacy import identity and record timestamps."
          >
            <DetailList columns={2}>
              <Detail label="Legacy id" mono>
                {product?.legacyId ?? '—'}
              </Detail>
              <Detail label="Legacy class" mono>
                {[product?.legacyClass, product?.legacySub].filter(Boolean).join(' / ') ||
                  '—'}
              </Detail>
              <Detail label="Created">{dateTime(product?.createdAt)}</Detail>
              <Detail label="Updated">{dateTime(product?.updatedAt)}</Detail>
            </DetailList>
          </Panel>

          <Panel title="Recent adjustments">
            {(product?.recentAdjustments ?? []).length === 0 ? (
              <p className="text-2xs text-ink-faint">No adjustments recorded.</p>
            ) : (
              <ul className="space-y-1">
                {(product?.recentAdjustments ?? []).map((a) => {
                  const delta = Number(a.quantityDelta ?? 0);
                  return (
                    <li key={a.id} className="flex items-baseline gap-2 text-xs">
                      <span className="text-2xs text-ink-faint w-24 shrink-0 font-mono tabular-nums">
                        {smartDate(a.occurredAt)}
                      </span>
                      <span className="text-ink-muted truncate">
                        {humanize(a.adjustmentType)}
                      </span>
                      {a.reason ? (
                        <span className="text-2xs text-ink-faint truncate">
                          {a.reason}
                        </span>
                      ) : null}
                      <span
                        className={`ml-auto font-mono tabular-nums ${
                          delta < 0 ? 'text-negative' : 'text-positive'
                        }`}
                      >
                        {delta > 0 ? '+' : ''}
                        {qty(delta)}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </Panel>

          <Panel title="Sales — last 90 days" className="xl:col-span-2">
            <DetailList columns={3} className="mb-3">
              <Detail label="Units" mono>
                {qty(product?.salesSummary?.units ?? 0)}
              </Detail>
              <Detail label="Revenue" mono>
                {money(product?.salesSummary?.revenue ?? 0)}
              </Detail>
              <Detail label="Avg price" mono>
                {money(product?.salesSummary?.avgPrice ?? 0)}
              </Detail>
            </DetailList>
            {salesDays.length === 0 ? (
              <p className="text-2xs text-ink-faint">No sales in the window.</p>
            ) : (
              <ul className="space-y-1">
                {salesDays.map((d) => (
                  <li key={d.salesDate} className="flex items-baseline gap-3 text-xs">
                    <span className="text-2xs text-ink-faint w-24 shrink-0 font-mono tabular-nums">
                      {smartDate(d.salesDate)}
                    </span>
                    <span className="text-ink w-16 shrink-0 text-right font-mono tabular-nums">
                      {qty(d.units)}
                    </span>
                    <span className="text-ink-muted w-20 shrink-0 text-right font-mono tabular-nums">
                      {money(d.revenue)}
                    </span>
                    <span className="text-2xs text-ink-faint">
                      {num(d.transactions ?? 0)} tickets
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Panel>
        </div>
      </PageBody>
      <Dialog
        open={askingPrice}
        onOpenChange={(open) => {
          if (!open) setAskingPrice(false);
        }}
        title="Give it a price first"
        description="A sold product needs a price above $0.00. The product goes back on the registers once saved."
        footer={
          <>
            <Button variant="ghost" onClick={() => setAskingPrice(false)}>
              Keep it as an input
            </Button>
            <Button
              variant="primary"
              disabled={priceToSell == null || priceToSell <= 0}
              onClick={() => {
                setDraft((prev) => ({
                  ...(prev ?? baseline!),
                  purchasingOnly: false,
                  price: priceToSell ?? 0,
                }));
                setAskingPrice(false);
              }}
            >
              Use this price
            </Button>
          </>
        }
      >
        <Field label="Price" required>
          <MoneyInput size="sm" value={priceToSell} onValueChange={setPriceToSell} />
        </Field>
      </Dialog>
    </Page>
  );
}

/* ── Barcodes ─────────────────────────────────────────────────────────────── */

function BarcodesPanel({
  productId,
  barcodes,
}: {
  productId: string;
  barcodes: ProductBarcode[];
}) {
  const queryClient = useQueryClient();
  const [code, setCode] = useState('');
  const [type, setType] = useState<string>('upc');
  const [primary, setPrimary] = useState(false);
  const [pendingRemove, setPendingRemove] = useState<ProductBarcode | null>(null);

  function refresh() {
    void queryClient.invalidateQueries({ queryKey: ['products'] });
    void queryClient.invalidateQueries({ queryKey: ['barcodes'] });
  }

  const add = useMutation({
    mutationFn: () =>
      api.post('/barcodes', {
        productId: Number(productId),
        code: code.trim(),
        type,
        isPrimary: primary,
      }),
    onSuccess: () => {
      toast.success(`Barcode ${code.trim()} added`);
      setCode('');
      setPrimary(false);
      refresh();
    },
    onError: (err) =>
      toast.error('Could not add barcode', { description: errorMessage(err) }),
  });

  const remove = useMutation({
    mutationFn: (barcodeId: number | string) => api.del(`/barcodes/${barcodeId}`),
    onSuccess: () => {
      toast.success('Barcode removed');
      setPendingRemove(null);
      refresh();
    },
    onError: (err) =>
      toast.error('Could not remove barcode', { description: errorMessage(err) }),
  });

  return (
    <Panel title="Barcodes" description={`${barcodes.length} active`}>
      {barcodes.length === 0 ? (
        <p className="text-2xs text-ink-faint">
          No barcode — this item can only be rung by search.
        </p>
      ) : (
        <ul className="mb-3 space-y-1">
          {barcodes.map((b) => (
            <li key={String(b.id)} className="flex items-center gap-2">
              <Code copyable>{b.code}</Code>
              <span className="text-2xs text-ink-faint tracking-wider uppercase">
                {b.type ?? 'upc'}
              </span>
              {b.isPrimary ? <Badge tone="signal">Primary</Badge> : null}
              <Button
                size="icon-sm"
                variant="ghost"
                className="ml-auto"
                title="Remove barcode"
                onClick={() => setPendingRemove(b)}
              >
                <Trash2 className="size-3.5" />
              </Button>
            </li>
          ))}
        </ul>
      )}

      <div className="border-line-soft flex items-end gap-2 border-t pt-3">
        <Field label="Add barcode" className="flex-1">
          <Input
            size="sm"
            mono
            value={code}
            placeholder="Scan or type…"
            onChange={(e) => setCode(e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && code.trim()) add.mutate();
            }}
          />
        </Field>
        <Field label="Type" className="w-28">
          <Select size="sm" value={type} onChange={(e) => setType(e.currentTarget.value)}>
            {BARCODE_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </Select>
        </Field>
        <Checkbox
          className="h-7"
          checked={primary}
          onCheckedChange={setPrimary}
          label="Primary"
        />
        <Button
          size="sm"
          icon={<Plus className="size-3.5" />}
          disabled={!code.trim()}
          loading={add.isPending}
          onClick={() => add.mutate()}
        >
          Add
        </Button>
      </div>

      <ConfirmDialog
        open={pendingRemove !== null}
        onOpenChange={(open) => {
          if (!open) setPendingRemove(null);
        }}
        title="Remove this barcode?"
        description={pendingRemove ? `Code ${pendingRemove.code}` : undefined}
        confirmLabel="Remove"
        loading={remove.isPending}
        consequences={[
          'The code stops scanning at every register on their next catalog sync.',
          'The row is tombstoned, not erased — it can be re-added later.',
        ]}
        onConfirm={() => {
          if (pendingRemove) remove.mutate(pendingRemove.id);
        }}
      />
    </Panel>
  );
}
